import * as React from 'react';
import { connect } from 'react-redux';
import { navigate } from 'libs/router';

import SortableTable from 'components/sortable-table/SortableTable';
import Pagination from 'components/pagination/Pagination';
import CloudinaryImage from 'components/CloudinaryImage';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import InfluencersAgencyAdminPanel
  from 'modules/admin/components/influencers-agencies/admin-panel/InfluencersAgencyAdminPanel';
import { fetchCurrentUserOrganizations } from 'modules/organizations/reducers/organizations';
import InfluencersFilter, {
  FILTER_BY_PROFILE_STATUS,
  InfluencerAgencyProfileQuery
} from '../influencers-filters/InfluencersFilter';
import {
  getCampaignStatusColumn,
  getChannelColumn,
  getContextMenuColumn,
  getCreatorColumn,
  getEngagementColumn,
  getFollowersColumn,
  getOverdueTasksColumn,
  getRegistrationColumn
} from './influencers-agency-overview';

import {
  deleteInfluencerProfile,
  fetchInfluencersAgency,
  getAgencyInfluencers
} from 'modules/influencers-agency/reducers/influencers-agencies-reducer';

import { isAdmin } from 'utils/account';
import { __t, applyTranslations } from 'utils/language';
import { getSortDirection } from 'modules/influencers-agency/utils';

import './influencers-agency-overview.scss';

applyTranslations(require('./influencers-agency-overview.i18n.json'));

import {
  AgencyInfluencersProfileOverview,
  InfluencersAgency,
  LocationWindow,
  PaginationLinks,
  PaginationMeta
} from 'types';

type StateProps = {
  influencerProfiles: Array<AgencyInfluencersProfileOverview>,
  location: LocationWindow,
  meta: PaginationMeta,
  links: PaginationLinks,
  agency: InfluencersAgency,
  isAdminMode: boolean
}

type State = {
  query: InfluencerAgencyProfileQuery
}

type OwnProps = {
  agencyId: number
};

type DispatchProps = {
  dispatchFetchProfiles: (query: InfluencerAgencyProfileQuery, agency: number) => Promise<void>,
  dispatchFetchInfluencersAgency: (agencyId: number) => Promise<void>,
  dispatchDeleteInfluencerProfile: (agencyId: number, profileId: number) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message: string,
      confirmButtonColor: 'destroy',
      confirmButtonLabel: string,
      cancelButtonLabel: string,
      onConfirm: () => Promise<void>,
      onCancel: () => Promise<void>
    }
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => Promise<void>,
  dispatchFetchCurrentUserOrganizations: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

class InfluencersAgencyOverview extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      query: {
        name: '',
        statuses: [],
        sort: 'name_asc',
        currentPage: 1
      }
    };
  }

  componentDidMount() {
    const { agencyId, dispatchFetchInfluencersAgency } = this.props;

    this.fetchProfiles();
    dispatchFetchInfluencersAgency(agencyId);
  }

  componentDidUpdate(prevProps: Props) {
    const { agencyId } = this.props;

    if (prevProps.agencyId !== agencyId) {
      this.fetchProfiles();
    }
  }

  deleteProfile = (id: number) => {
    const {
      agencyId,
      influencerProfiles,
      dispatchDeleteInfluencerProfile,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchFetchCurrentUserOrganizations
    } = this.props;
    const profile = influencerProfiles.find(v => v.id === id) ?? null;

    if (profile === null) {
      return;
    }

    dispatchShowConfirmationModal({
      title: __t(
        'influencers_agency_overview.delete_confirmation_modal.title',
        { profileName: profile.name }
      ),
      message: __t('influencers_agency_overview.delete_confirmation_modal.message'),
      confirmButtonColor: 'destroy',
      cancelButtonLabel: __t(
        'influencers_agency_overview.delete_confirmation_modal.cancel_button'
      ),
      confirmButtonLabel: __t(
        'influencers_agency_overview.delete_confirmation_modal.confirm_button'
      ),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: () => Promise.resolve()
        .then(() => dispatchDeleteInfluencerProfile(agencyId, id))
        .then(() => dispatchHideConfirmationModal())
        .then(() => {
          Promise.all([
            this.fetchProfiles(),
            dispatchFetchCurrentUserOrganizations()
          ]);
        })
    });
  };

  getColumns() {
    return [
      getCreatorColumn(),
      getRegistrationColumn(),
      getChannelColumn(),
      getFollowersColumn(),
      getEngagementColumn(),
      getCampaignStatusColumn(),
      getOverdueTasksColumn(),
      getContextMenuColumn(this.deleteProfile)
    ];
  }

  handlePageChange = (currentPage: number) => {
    this.setState(prevState => ({
      ...prevState,
      query: {
        ...prevState.query,
        currentPage
      }
    }), this.fetchProfiles);
  };

  fetchProfiles = () => {
    const { query } = this.state;
    const { agencyId, dispatchFetchProfiles } = this.props;

    const params = {
      ...query,
      sort: getSortDirection(query.sort) || 'name.asc',
      statuses: query.statuses.length ? query.statuses : FILTER_BY_PROFILE_STATUS
    };

    dispatchFetchProfiles(params, agencyId);
  };

  render() {
    const { query } = this.state;
    const { agencyId, influencerProfiles, isAdminMode, location, meta, links, agency } = this.props;

    return (
      <div>
        { isAdminMode && <InfluencersAgencyAdminPanel agency={agency} /> }
        <div className="influencers-agency-overview__agency-name">
          {agency.name}
        </div>
        <CloudinaryImage
          width={936}
          height={240}
          image={agency.coverImage}
        />
        <InfluencersFilter
          onUpdate={(q: InfluencerAgencyProfileQuery) => {
            this.setState(prevState => ({
              ...prevState,
              query: q
            }), this.fetchProfiles);
          }}
          onAddProfile={() => {
            navigate.to(`/influencers-agencies/${agencyId}/settings/add-new-profiles`);
          }}
          filterOptions={query}
        />
        <div className="influencers-agency-overview__number-of-profiles">
          {meta?.itemsTotal || 0}
          <span>{__t('influencers_agency_overview.number_of_profiles',
            { withPlural: meta?.itemsTotal || 1 })}
          </span>
        </div>
        <SortableTable
          key={influencerProfiles.length}
          layout="dense"
          customClass="influencers-agency-overview__table"
          data={influencerProfiles}
          columns={this.getColumns()}
          emptyMessage={__t('influencers_agency_overview.empty_message')}
          onRowClicked={(item) => {
            window.open(`/content-creator/profiles/${item.id}`, '_blank');
          }}
        />
        <Pagination
          key="pagination-for-influencers-agencies-profile"
          location={location}
          meta={meta}
          links={links}
          onClick={this.handlePageChange}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ influencersAgency, router, account }): StateProps => ({
  location: router.location,
  influencerProfiles: influencersAgency.influencerProfiles.data,
  meta: influencersAgency.influencerProfiles.meta,
  links: influencersAgency.influencerProfiles.links,
  agency: influencersAgency.profile,
  isAdminMode: isAdmin(account.currentAccount, account.roles)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: props => dispatch(showConfirmModal(props)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchFetchProfiles: (query, agencyId) => dispatch(getAgencyInfluencers(query, agencyId)),
  dispatchFetchInfluencersAgency: (agencyId: number) => dispatch(fetchInfluencersAgency(agencyId)),
  dispatchDeleteInfluencerProfile: (agencyId, profileId) =>
    dispatch(deleteInfluencerProfile(agencyId, profileId)),
  dispatchFetchCurrentUserOrganizations: () => dispatch(fetchCurrentUserOrganizations())
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencersAgencyOverview);
